/**
 * Home page sections: Trending, Join Society, Popular Businesses, Explore Pakistan, Properties Near You
 * (compact horizontal strips on small viewports, uniform card height, bottom-aligned CTAs, 3px bottom inset),
 * Upcoming Events (same), Footer CTA. Each list section heading includes a right-aligned View more link.
 * All data from backend APIs.
 * Events / Properties / Explore hide when empty after load.
 * Join society: strong profiles open full page; weak open preview modal (noindex pages).
 * Tailwind only; rounded-2xl, shadow-sm, green accent.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { societyService } from '../../../services/societyService';
import pointService from '../../../services/pointService';
import { getPointCardImageUrl } from '../../../pages/points/pointImageUtils';
import { businessService } from '../../../services/businessService';
import { eventService } from '../../../services/eventService';
import propertyService from '../../../services/propertyService';
import trendingService from '../../../services/trendingService';
import { getSocietyUrl, getBusinessUrl, getEventUrl, getPropertyUrl } from '../../../utils/urlHelpers';
import { shouldOpenSocietyAsFullPage } from '../../../utils/societySeo';
import SocietyPreviewModal from '../../societies/SocietyPreviewModal';

function parseList(res) {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  return res.results || res.data?.results || [];
}

/** Shared home-section card strip: horizontal scroll on small viewports, grid on md+. */
const HOME_CARD_ROW_3COL =
  'flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory sm:snap-none md:overflow-visible md:grid md:grid-cols-3 md:gap-4 md:snap-none [-webkit-overflow-scrolling:touch]';

const HOME_CARD_ROW_3_4COL =
  'flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory sm:snap-none md:overflow-visible md:grid md:grid-cols-3 md:gap-4 lg:grid-cols-4 lg:gap-4 md:snap-none [-webkit-overflow-scrolling:touch]';

const HOME_CARD_SKELETON_DIMS =
  'h-[256px] sm:h-[264px] md:h-[268px] flex-shrink-0 w-[168px] sm:w-[184px] md:w-auto snap-start rounded-xl bg-gray-100 animate-pulse';

const HOME_ARTICLE_SHELL =
  'flex h-[256px] sm:h-[264px] md:h-[268px] min-h-0 w-[168px] shrink-0 snap-start flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow sm:w-[184px] md:w-auto';

const HOME_CARD_BODY =
  'flex min-h-0 min-w-0 flex-1 flex-col px-2.5 pt-2.5 pb-[3px] sm:px-3 sm:pt-3 sm:pb-[3px]';

const HOME_CARD_IMAGE_WRAP = 'relative h-24 shrink-0 overflow-hidden group bg-gray-100 cursor-pointer';

/** Fallback when a home card has no image (served from `public/images/`). */
const HOME_CARD_IMAGE_PLACEHOLDER = '/images/pakpoints-placeholder.png';

/**
 * Section title (h2) with a right-aligned “View more” link to the full listing route.
 * @param {string} id - Element id for aria-labelledby on the section.
 * @param {React.ReactNode} [icon] - Decorative icon (wrap with aria-hidden in caller if needed).
 * @param {string} title - Visible heading text.
 * @param {string} moreTo - react-router path for View more.
 * @param {string} [moreLabel] - Link text (default View more).
 * @param {string} [moreAriaLabel] - Accessible name for the link.
 * @param {string} [rowClassName] - Tailwind classes on the title row wrapper (e.g. mb-2).
 */
function SectionTitleRow({ id, icon, title, moreTo, moreLabel = 'View more', moreAriaLabel, rowClassName = '' }) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 ${rowClassName}`.trim()}>
      <h2 id={id} className="flex min-w-0 flex-1 items-center gap-2 text-2xl font-bold text-gray-900">
        {icon != null ? <span className="shrink-0">{icon}</span> : null}
        <span className="min-w-0">{title}</span>
      </h2>
      <Link
        to={moreTo}
        aria-label={moreAriaLabel || `${moreLabel}: ${title}`}
        className="shrink-0 rounded-md text-sm font-semibold text-emerald-600 hover:text-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
      >
        {moreLabel}
      </Link>
    </div>
  );
}

function CardImage({ src, alt = '' }) {
  return (
    <img
      src={src || HOME_CARD_IMAGE_PLACEHOLDER}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
    />
  );
}

export function TrendingAcrossSection() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const results = await trendingService.getCountryTrending();
        if (cancelled) return;
        const list = (results || []).map((r) => ({
          type: 'business',
          ...(r.business_data || {}),
        }));
        setItems(list);
      } catch (_) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const getImage = (item) => item.display_image?.original_image || item.images?.[0]?.original_image;
  const getTitle = (item) => item.name || item.title || '';
  const getLocation = (item) => item.society?.city || item.city || item.venue || '';
  const getStat = (item) => `${(item.views || 0).toLocaleString()} Views`;
  const onView = (item) => navigate(getBusinessUrl(item));

  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <section className="py-10 sm:py-14" aria-labelledby="trending-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="trending-heading"
          icon={<span aria-hidden="true">🔥</span>}
          title="Trending Across Pakistan"
          moreTo="/businesses"
          moreAriaLabel="View more businesses"
          rowClassName="mb-6"
        />
        {loading ? (
          <div className="flex gap-4 overflow-x-auto pb-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-[280px] rounded-2xl bg-gray-100 animate-pulse aspect-[4/3]" />
            ))}
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2 md:overflow-visible md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6">
            {items.map((item) => (
              <article key={`${item.type}-${item.id}`} className="flex-shrink-0 w-[280px] md:w-auto rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="relative aspect-video overflow-hidden group bg-gray-100" onClick={() => onView(item)}>
                  <CardImage src={getImage(item)} />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 line-clamp-1">{getTitle(item)}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{getLocation(item)}</p>
                  <p className="text-sm text-gray-600 mt-1">{getStat(item)}</p>
                  <button type="button" onClick={() => onView(item)} className="mt-3 w-full py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors">
                    View Details
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function JoinSocietySection() {
  const navigate = useNavigate();
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewSociety, setPreviewSociety] = useState(null);

  useEffect(() => {
    let cancelled = false;
    societyService.getSocieties({ page_size: 6 }).then((res) => {
      if (!cancelled) setSocieties(parseList(res));
    }).catch(() => { if (!cancelled) setSocieties([]); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const getImage = (s) => s.images?.[0]?.original_image;
  const members = (s) => (s.follower_count ?? 0).toLocaleString();

  const openSociety = (s) => {
    if (shouldOpenSocietyAsFullPage(s)) {
      navigate(getSocietyUrl(s));
      return;
    }
    setPreviewSociety(s);
  };

  return (
    <section className="py-10 sm:py-14 bg-gray-50/50" aria-labelledby="join-society-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="join-society-heading"
          icon={<span aria-hidden="true">🏘</span>}
          title="Join a Society Near You"
          moreTo="/societies"
          moreAriaLabel="View more societies"
          rowClassName="mb-2"
        />
        <p className="text-gray-600 mb-6">Find your residential community and connect with residents.</p>
        {loading ? (
          <div className={HOME_CARD_ROW_3COL} aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={HOME_CARD_SKELETON_DIMS} />
            ))}
          </div>
        ) : (
          <div className={HOME_CARD_ROW_3COL} role="list" aria-label="Societies you can join">
            {societies.map((s) => (
              <article key={s.id} role="listitem" className={HOME_ARTICLE_SHELL}>
                <div
                  className={HOME_CARD_IMAGE_WRAP}
                  onClick={() => openSociety(s)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openSociety(s);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${s.name}`}
                >
                  <CardImage src={getImage(s)} alt="" />
                </div>
                <div className={HOME_CARD_BODY}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{s.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{s.city || ''}</p>
                  {shouldOpenSocietyAsFullPage(s) ? (
                    <p className="text-xs text-gray-600 mt-0.5">{members(s)} Members</p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-0.5">Limited profile</p>
                  )}
                  <button
                    type="button"
                    onClick={() => openSociety(s)}
                    className="mt-auto flex w-full shrink-0 items-center justify-center rounded-b-xl rounded-t-lg border border-emerald-500 py-2 text-emerald-600 text-xs font-medium hover:bg-emerald-50 transition-colors min-h-[44px]"
                  >
                    {shouldOpenSocietyAsFullPage(s) ? 'Join' : 'Preview'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <SocietyPreviewModal
        open={!!previewSociety}
        society={previewSociety}
        onClose={() => setPreviewSociety(null)}
        onContinue={(s) => {
          setPreviewSociety(null);
          navigate(getSocietyUrl(s));
        }}
      />
    </section>
  );
}

export function PopularBusinessesSection() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    businessService.getBusinesses({ page_size: 6, is_public: true }).then((res) => {
      if (!cancelled) setBusinesses(parseList(res));
    }).catch(() => { if (!cancelled) setBusinesses([]); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const getImage = (b) => b.display_image?.original_image || b.images?.[0]?.original_image;
  const city = (b) => b.society?.city || '';

  return (
    <section className="py-10 sm:py-14" aria-labelledby="popular-businesses-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="popular-businesses-heading"
          icon={<span aria-hidden="true">🏢</span>}
          title="Popular Businesses"
          moreTo="/businesses"
          moreAriaLabel="View more businesses"
          rowClassName="mb-2"
        />
        <p className="text-gray-600 mb-6">Discover trusted local businesses in your area.</p>
        {loading ? (
          <div className={HOME_CARD_ROW_3COL} aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={HOME_CARD_SKELETON_DIMS} />
            ))}
          </div>
        ) : (
          <div className={HOME_CARD_ROW_3COL} role="list" aria-label="Popular businesses">
            {businesses.map((b) => (
              <article key={b.id} role="listitem" className={HOME_ARTICLE_SHELL}>
                <div
                  className={HOME_CARD_IMAGE_WRAP}
                  onClick={() => navigate(getBusinessUrl(b))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(getBusinessUrl(b));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${b.name}`}
                >
                  <CardImage src={getImage(b)} alt="" />
                </div>
                <div className={HOME_CARD_BODY}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{b.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{city(b)}</p>
                  {b.views != null && (
                    <p className="text-xs text-gray-600 mt-0.5">{Number(b.views).toLocaleString()} views</p>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(getBusinessUrl(b))}
                    className="mt-auto flex w-full shrink-0 items-center justify-center rounded-b-xl rounded-t-lg bg-emerald-600 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-700 min-h-[44px]"
                  >
                    View Profile
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function EventsThisWeekSection() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    eventService.getEvents({ page_size: 6, ordering: 'start_date' }).then((res) => {
      if (!cancelled) setEvents(parseList(res));
    }).catch(() => { if (!cancelled) setEvents([]); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const getImage = (e) => e.images?.[0]?.original_image;
  const dateStr = (e) => {
    if (!e.start_date) return '';
    try {
      const d = new Date(e.start_date);
      return d.toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (_) { return e.start_date; }
  };
  const city = (e) => e.society?.city || e.venue || '';

  // Hide empty inventory sections so crawlers/AdSense do not see "No events found".
  if (!loading && events.length === 0) {
    return null;
  }

  return (
    <section className="py-10 sm:py-14 bg-gray-50/50" aria-labelledby="events-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="events-heading"
          icon={<span aria-hidden="true">📅</span>}
          title="Upcoming Events"
          moreTo="/events"
          moreAriaLabel="View more events"
          rowClassName="mb-2"
        />
        <p className="text-gray-600 mb-6">See what is happening soon in communities near you.</p>
        {loading ? (
          <div className={HOME_CARD_ROW_3COL} aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={HOME_CARD_SKELETON_DIMS} />
            ))}
          </div>
        ) : (
          <div className={HOME_CARD_ROW_3COL} role="list" aria-label="Upcoming events">
            {events.map((e) => (
              <article key={e.id} role="listitem" className={HOME_ARTICLE_SHELL}>
                <div
                  className={HOME_CARD_IMAGE_WRAP}
                  onClick={() => navigate(getEventUrl(e))}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      navigate(getEventUrl(e));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${e.name}`}
                >
                  <CardImage src={getImage(e)} alt="" />
                </div>
                <div className={HOME_CARD_BODY}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{e.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{dateStr(e)}</p>
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{city(e)}</p>
                  <button
                    type="button"
                    onClick={() => navigate(getEventUrl(e))}
                    className="mt-auto flex w-full shrink-0 items-center justify-center rounded-b-xl rounded-t-lg bg-emerald-600 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-700 min-h-[44px]"
                  >
                    View Details
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function ExplorePakistanSection() {
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    pointService
      .getPoints({ page_size: 8, ordering: '-is_featured,-average_rating' })
      .then((res) => {
        if (!cancelled) setPoints(parseList(res));
      })
      .catch(() => {
        if (!cancelled) setPoints([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getPointUrl = (p) => (p.slug ? `/points/${p.slug}` : '/points');
  const locationLine = (p) => [p.area, p.city].filter(Boolean).join(' · ') || 'Pakistan';
  const metaLine = (p) =>
    p.point_category?.name ||
    (p.total_reviews != null ? `${p.total_reviews} review${p.total_reviews === 1 ? '' : 's'}` : '');

  if (!loading && points.length === 0) {
    return null;
  }

  return (
    <section className="py-10 sm:py-14" aria-labelledby="explore-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="explore-heading"
          icon={<span aria-hidden="true">📍</span>}
          title="Explore Pakistan"
          moreTo="/points"
          moreAriaLabel="View more places to explore"
          rowClassName="mb-2"
        />
        <p className="text-gray-600 mb-6">Discover destinations and attractions across Pakistan.</p>
        {loading ? (
          <div className={HOME_CARD_ROW_3_4COL} aria-hidden="true">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className={HOME_CARD_SKELETON_DIMS} />
            ))}
          </div>
        ) : (
          <div className={HOME_CARD_ROW_3_4COL} role="list" aria-label="Places to explore in Pakistan">
            {points.map((p) => (
              <article key={p.id} role="listitem" className={HOME_ARTICLE_SHELL}>
                <div
                  className={HOME_CARD_IMAGE_WRAP}
                  onClick={() => navigate(getPointUrl(p))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(getPointUrl(p));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${p.name}`}
                >
                  <CardImage src={getPointCardImageUrl(p)} alt="" />
                </div>
                <div className={HOME_CARD_BODY}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{locationLine(p)}</p>
                  {metaLine(p) ? (
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{metaLine(p)}</p>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => navigate(getPointUrl(p))}
                    className="mt-auto flex w-full shrink-0 items-center justify-center rounded-b-xl rounded-t-lg bg-emerald-600 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-700 min-h-[44px]"
                  >
                    View details
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function PropertiesNearSection() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    propertyService.getProperties({ page_size: 6 }).then((res) => {
      if (!cancelled) setProperties(parseList(res));
    }).catch(() => { if (!cancelled) setProperties([]); }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const getImage = (p) => p.images?.[0]?.original_image;
  const location = (p) => p.society?.city || p.city || p.address || '';

  // Hide empty inventory sections so crawlers/AdSense do not see "No properties found".
  if (!loading && properties.length === 0) {
    return null;
  }

  return (
    <section className="py-10 sm:py-14 bg-gray-50/50" aria-labelledby="properties-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitleRow
          id="properties-heading"
          icon={<span aria-hidden="true">🏠</span>}
          title="Properties Near You"
          moreTo="/properties"
          moreAriaLabel="View more properties"
          rowClassName="mb-2"
        />
        <p className="text-gray-600 mb-6">Browse listings in communities you care about.</p>
        {loading ? (
          <div className={HOME_CARD_ROW_3COL} aria-hidden="true">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={HOME_CARD_SKELETON_DIMS} />
            ))}
          </div>
        ) : (
          <div className={HOME_CARD_ROW_3COL} role="list" aria-label="Properties near you">
            {properties.map((p) => (
              <article key={p.id} role="listitem" className={HOME_ARTICLE_SHELL}>
                <div
                  className={HOME_CARD_IMAGE_WRAP}
                  onClick={() => navigate(getPropertyUrl(p))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(getPropertyUrl(p));
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${p.title || p.name || 'property'}`}
                >
                  <CardImage src={getImage(p)} alt="" />
                </div>
                <div className={HOME_CARD_BODY}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">{p.title || p.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">{location(p)}</p>
                  {(p.bedrooms != null || p.bathrooms != null) && (
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                      {[p.bedrooms != null && `${p.bedrooms} Beds`, p.bathrooms != null && `${p.bathrooms} Baths`].filter(Boolean).join(' / ')}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(getPropertyUrl(p))}
                    className="mt-auto flex w-full shrink-0 items-center justify-center rounded-b-xl rounded-t-lg bg-emerald-600 py-2 text-xs font-medium text-white transition-colors hover:bg-emerald-700 min-h-[44px]"
                  >
                    View Details
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function FooterCTASection() {
  const navigate = useNavigate();
  return (
    <section className="py-14 sm:py-18" aria-labelledby="footer-cta-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p id="footer-cta-heading" className="text-lg text-gray-600 mb-6">Be part of Pakistan&apos;s growing network.</p>
        <button type="button" onClick={() => navigate('/societies')} className="rounded-xl bg-emerald-600 text-white px-8 py-3 font-medium hover:bg-emerald-700 transition-colors">
          Join Now
        </button>
      </div>
    </section>
  );
}
